// Must run before anything that may call crypto.randomUUID (undefined on
// plain-HTTP / non-secure contexts like the IP-mode demo).
import './utils/cryptoPolyfill'

import React from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { PROD_MODE_ENABLED, SENTRY_DSN } from './config/runtimeConfig'

// Initialize i18n BEFORE React components
import './i18n/config'

import DashboardPage from './pages/DashboardPage'
import PaymentCompletion from './pages/PaymentCompletion'
import LoginPage from './pages/LoginPage'
import LogoutPage from './pages/LogoutPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import ConfirmPasswordPage from './pages/ConfirmPasswordPage'
import SignupPage from './pages/SignupPage'
import ActivateAccountPage from './pages/ActivateAccountPage'
import OAuthCallbackPage from './pages/OAuthCallbackPage'
import MfaChallengePage from './pages/MfaChallengePage'
import SignupSuccessPage from './pages/SignupSuccessPage'
import CostsPage from './pages/CostsPage'

import { PrivateRoute, PublicRoute } from './components/Auth/AuthGuards'
import CookieConsentBanner from './components/CookieConsentBanner/CookieConsentBanner'
import RootErrorBoundary from './components/ui/RootErrorBoundary'
import ToastsContainer from './components/ui/ToastsContainer'

import SettingsContextProvider from './core/contexts/SettingsContext.jsx'
import ToastsContextProvider, {
  ToastsContext,
} from './core/contexts/ToastsContext.jsx'
import UserContextProvider from './core/contexts/UserContext.jsx'
import PaymentContextProvider from './contexts/PaymentContext.jsx'

import './index.css'
import { captureRechargeIntent } from './utils/rechargeFromVm'

// Prima del router: se la scheda arriva da una VM (?recharge=1) e deve passare
// dal login, il redirect a /login perde il parametro.
captureRechargeIntent()

// Nella webapp LeaRes il ToastsContainer vive dentro GView (vista autenticata):
// nel portale non c'è GView, quindi va montato globalmente o nessun toast
// (errori di login inclusi) sarebbe visibile.
const GlobalToasts = () => {
  const { toasts, setToasts, removeToast } = React.useContext(ToastsContext)
  if (!toasts?.length) return null
  return (
    <div className="fixed bottom-4 left-4 z-[10001]">
      <ToastsContainer
        toasts={toasts}
        setToasts={setToasts}
        removeToast={removeToast}
      />
    </div>
  )
}

if (
  +PROD_MODE_ENABLED &&
  SENTRY_DSN
) {
  Sentry.init({
    dsn: SENTRY_DSN,
    // PRIVACY: stesso setup della webapp LeaRes — nessun PII, testo mascherato.
    sendDefaultPii: false,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        maskAllInputs: true,
      }),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })
}

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: (
        <PrivateRoute>
          <DashboardPage />
        </PrivateRoute>
      ),
    },
    {
      // Dashboard esplicita (crediti/ricarica/logout), senza auto-redirect
      path: '/account',
      element: (
        <PrivateRoute>
          <DashboardPage />
        </PrivateRoute>
      ),
    },
    {
      path: '/payment-completion',
      element: (
        <PrivateRoute>
          <PaymentCompletion />
        </PrivateRoute>
      ),
    },
    {
      path: '/login',
      element: (
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      ),
    },
    {
      // Senza guard: deve pulire la sessione del portale PRIMA che
      // PublicRoute valuti il cookie, altrimenti rimbalza sulla dashboard.
      path: '/logout',
      element: <LogoutPage />,
    },
    {
      path: '/oauth-callback',
      element: <OAuthCallbackPage />,
    },
    {
      // Plans and costs (ticket 6ad5d51d): public, no guard. Without a session it is
      // the login page opened on its Info & costs section; with one, the same
      // overview with the way back to the account. PublicRoute would bounce a
      // logged-in user to "/", which hands off to the VM.
      path: '/costs',
      element: <CostsPage />,
    },
    {
      path: '/mfa-challenge',
      element: <MfaChallengePage />,
    },
    {
      path: '/password-reset',
      element: (
        <PublicRoute>
          <ForgotPasswordPage />
        </PublicRoute>
      ),
    },
    {
      path: '/password-reset/:token',
      element: (
        <PublicRoute>
          <ResetPasswordPage />
        </PublicRoute>
      ),
    },
    {
      path: '/confirm-password',
      element: (
        <PublicRoute>
          <ConfirmPasswordPage />
        </PublicRoute>
      ),
    },
    {
      path: '/signup',
      element: (
        <PublicRoute>
          <SignupPage />
        </PublicRoute>
      ),
    },
    {
      path: '/signup-success',
      element: (
        <PublicRoute>
          <SignupSuccessPage />
        </PublicRoute>
      ),
    },
    {
      path: '/activate',
      element: (
        <PublicRoute>
          <ActivateAccountPage />
        </PublicRoute>
      ),
    },
    {
      path: '*',
      element: (
        <PrivateRoute>
          <Navigate to="/" />
        </PrivateRoute>
      ),
    },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  }
)

const root = ReactDOM.createRoot(document.getElementById('root'))
root.render(
  <RootErrorBoundary>
    <ToastsContextProvider>
      <UserContextProvider>
        <SettingsContextProvider>
          <PaymentContextProvider>
            <RouterProvider router={router} />
            <GlobalToasts />
            <CookieConsentBanner />
          </PaymentContextProvider>
        </SettingsContextProvider>
      </UserContextProvider>
    </ToastsContextProvider>
  </RootErrorBoundary>
)
